// src/pages/MovesPage.js (full updated)
import React, { useEffect, useMemo, useState } from "react";
import { api } from "../scripts/api";
import CardComponent from "../components/CardComponent";
import Modal from "../components/Modal";

function toNumId(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export default function MovesPage() {
  const [moves, setMoves] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [drvById, setDrvById] = useState({});
  const [selectedMove, setSelectedMove] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  // inventory hydration cache: { [id]: record|null }
  const [invById, setInvById] = useState({});

  // Form fields for create/edit
  const [destination, setDestination] = useState("");
  const [airline, setAirline] = useState("");
  const [flightNum, setFlightNum] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [permits, setPermits] = useState("");
  const [driverId, setDriverId] = useState("");
  const [notes, setNotes] = useState("");
  const [inventoryId, setInventoryId] = useState("");
  const [rowo, setRowo] = useState("");
  const [chassisVin, setChassisVin] = useState("");

  // Search modal state
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchBusy, setSearchBusy] = useState(false);

  useEffect(() => {
    loadMoves();
    loadDrivers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hydrateInventoryForMoves = async (list) => {
    const ids = Array.from(
      new Set(
        (list || [])
          .map((m) => toNumId(m.inventory_id))
          .filter((n) => n != null)
      )
    );

    // fetch only missing ids
    const missing = ids.filter((id) => !(id in invById));
    if (!missing.length) return;

    // parallel fetch; store null on failure to avoid retry loops
    const fetched = await Promise.all(
      missing.map(async (id) => {
        try {
          const { data } = await api.get(`/all_inventory/${id}`);
          return [id, data || null];
        } catch {
          return [id, null];
        }
      })
    );

    setInvById((prev) => {
      const next = { ...prev };
      for (const [id, rec] of fetched) next[id] = rec;
      return next;
    });
  };

  const loadMoves = async () => {
    setBusy(true);
    setMsg("");
    try {
      const { data } = await api.get("/moves");
      const list = Array.isArray(data) ? data : [];
      setMoves(list);
      await hydrateInventoryForMoves(list);
    } catch (e) {
      setMsg(e?.response?.data?.error || "Failed to load moves.");
      setMoves([]);
    } finally {
      setBusy(false);
    }
  };

  const loadDrivers = async () => {
    try {
      const { data } = await api.get("/driver");
      const list = Array.isArray(data) ? data : [];
      setDrivers(list);
      const byId = {};
      for (const d of list) byId[d.DriverId] = d;
      setDrvById(byId);
    } catch (e) {
      console.error("Failed to load drivers", e);
    }
  };

  const createOrUpdateMove = async () => {
    if (!inventoryId) {
      setMsg("Inventory ID required.");
      return;
    }

    const payload = {
      Destination: destination,
      Airline: airline,
      FlightNum: flightNum,
      Confirmation: confirmation,
      Permits: permits,
      driver_id: toNumId(driverId) || null,
      Notes: notes,
      inventory_id: inventoryId,
      rowo: rowo,
      chassis_vin: chassisVin,
    };

    setBusy(true);
    setMsg("");
    try {
      if (editMode) {
        await api.put(`/moves/${selectedMove.TripId}`, payload);
        setMsg("Move updated successfully.");
      } else {
        await api.post("/moves", payload);
        setMsg("Move created successfully.");
      }

      // refresh list + ensure the selected inventory record is hydrated
      resetForm();
      await loadMoves();
      closeModal();
    } catch (e) {
      setMsg(e?.response?.data?.error || "Operation failed.");
    } finally {
      setBusy(false);
    }
  };

  const deleteMove = async (id) => {
    if (!window.confirm("Delete this move?")) return;
    setBusy(true);
    try {
      await api.delete(`/moves/${id}`);
      setMsg("Move deleted.");
      await loadMoves();
    } catch (e) {
      setMsg(e?.response?.data?.error || "Delete failed.");
    } finally {
      setBusy(false);
    }
  };

  const endMove = async (moveId, invId, statusCode) => {
    if (!window.confirm(`Confirm: Set inventory status to ${statusCode} and end move?`)) return;
    setBusy(true);
    setMsg("");
    try {
      // Update inventory status
      await api.put(`/all_inventory/${invId}`, { color_code: statusCode });

      // Delete move
      await api.delete(`/moves/${moveId}`);

      setMsg("Move ended successfully.");
      await loadMoves();
    } catch (e) {
      setMsg(e?.response?.data?.error || "Failed to end move.");
    } finally {
      setBusy(false);
    }
  };

  const openModal = async (move = null) => {
    setEditMode(!!move);

    if (move) {
      setDestination(move.destination || "");
      setAirline(move.Airline || "");
      setFlightNum(move.FlightNum || "");
      setConfirmation(move.Confirmation || "");
      setPermits(move.Permits || "");
      setDriverId(move.driver_id || "");
      setNotes(move.Notes || "");
      setInventoryId(move.inventory_id || "");
      setRowo(move.rowo || "");
      setChassisVin(move.chassis_vin || "");
    } else {
      resetForm();
    }

    setSelectedMove(move || {});

    // hydrate for edit preview too
    const invId = toNumId(move?.inventory_id);
    if (invId != null && !(invId in invById)) {
      try {
        const { data } = await api.get(`/all_inventory/${invId}`);
        setInvById((prev) => ({ ...prev, [invId]: data || null }));
      } catch {
        setInvById((prev) => ({ ...prev, [invId]: null }));
      }
    }
  };

  const closeModal = () => {
    setSelectedMove(null);
  };

  const resetForm = () => {
    setDestination("");
    setAirline("");
    setFlightNum("");
    setConfirmation("");
    setPermits("");
    setDriverId("");
    setNotes("");
    setInventoryId("");
    setRowo("");
    setChassisVin("");
  };

  // Search functions
  const searchInventory = async () => {
    if (!searchQuery.trim()) return;
    setSearchBusy(true);
    try {
      const { data } = await api.get("all_inventory", { params: { q: searchQuery } });
      setSearchResults(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Search failed", e);
      setSearchResults([]);
    } finally {
      setSearchBusy(false);
    }
  };

  const selectInventory = async (record) => {
    const id = record?.id ?? "";
    setInventoryId(id);
    setRowo(record?.rowo || "");
    setChassisVin(record?.chassis_vin || "");
    setShowSearchModal(false);

    const invId = toNumId(id);
    if (invId != null) {
      // we already have the record from search results; cache it
      setInvById((prev) => ({ ...prev, [invId]: record || null }));
    }
  };

  const moveCards = useMemo(() => {
    return moves.map((m) => {
      const invId = toNumId(m.inventory_id);
      const invRec = invId != null ? invById[invId] : null;
      return { m, invId, invRec };
    });
  }, [moves, invById]);

  const selectedInvId = toNumId(selectedMove?.inventory_id);
  const selectedInvRec = selectedInvId != null ? invById[selectedInvId] : null;

  return (
    <div className="container">
      <div
        className="row"
        style={{ justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}
      >
        <h2 style={{ margin: 0, fontWeight: 900 }}>Drive-Away Operations</h2>
        <button className="btn-outline" onClick={loadMoves} disabled={busy}>
          Refresh
        </button>
        <button className="btn" onClick={() => openModal()} disabled={busy}>
          Add A New Move
        </button>
      </div>

      {msg ? (
        <div className="muted" style={{ marginTop: 10, whiteSpace: "pre-wrap" }}>
          {msg}
        </div>
      ) : null}

      <div className="card" style={{ marginTop: 12 }}>
        <div style={{ fontWeight: 900, marginBottom: 10 }}>Moves In Progress</div>
        {busy ? <div className="muted">Loading…</div> : null}

        <div style={{ display: "grid", gap: 10 }}>
          {moveCards.map(({ m, invId, invRec }) => (
            <div key={m.TripId} className="card" style={{ background: "rgba(255,255,255,0.02)", position: "relative" }}>
              <div
                className="row"
                style={{ justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}
              >
                <div>
                  <div style={{ fontWeight: 900 }}>Move #{m.TripId}</div>
                  <div className="muted" style={{ marginTop: 4 }}>
                    Inventory Id: {m.inventory_id || "(none)"} • Location: {m.destination || "(none)"} • Driver:{" "}
                    {drvById[m.driver_id]?.FullName || "(none)"}
                  </div>
                </div>

                <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                  <button className="btn-outline" onClick={() => openModal(m)} disabled={busy}>
                    Edit
                  </button>
                  <button className="btn-outline" onClick={() => deleteMove(m.TripId)} disabled={busy}>
                    Delete
                  </button>
                </div>
              </div>

              <div style={{ marginTop: 10 }}>
                {/* ✅ render full inventory record when available */}
                <CardComponent record={invRec || (invId != null ? { id: invId } : {})} table="all_inventory" />
                {invId != null && invRec === null ? (
                  <div className="muted" style={{ marginTop: 8 }}>
                    Couldn’t load inventory record #{invId}.
                  </div>
                ) : null}
              </div>

              {/* Hover Overlay */}
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  background: "rgba(0,0,0,0.5)",
                  display: "none",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 10,
                  flexDirection: "column",
                }}
                className="overlay"
                onMouseEnter={(e) => e.currentTarget.style.display = "flex"}
                onMouseLeave={(e) => e.currentTarget.style.display = "none"}
              >
                <button className="btn" onClick={() => endMove(m.TripId, m.inventory_id, 13)} disabled={busy}>
                  Broke Down
                </button>
                <button className="btn" onClick={() => endMove(m.TripId, m.inventory_id, 3)} disabled={busy}>
                  Complete Move
                </button>
              </div>
            </div>
          ))}

          {!busy && moves.length === 0 ? <div className="muted">No moves found.</div> : null}
        </div>
      </div>

      {selectedMove && (
        <Modal onClose={closeModal}>
          <h2>{editMode ? "Edit Move" : "Create Move"}</h2>

          <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
            <input className="input" placeholder="Destination" value={destination} onChange={(e) => setDestination(e.target.value)} />
            <input className="input" placeholder="Airline" value={airline} onChange={(e) => setAirline(e.target.value)} />
            <input className="input" placeholder="Flight Num" value={flightNum} onChange={(e) => setFlightNum(e.target.value)} />
            <input className="input" placeholder="Confirmation" value={confirmation} onChange={(e) => setConfirmation(e.target.value)} />
            <input className="input" placeholder="Permits" value={permits} onChange={(e) => setPermits(e.target.value)} />

            <select className="input" value={driverId} onChange={(e) => setDriverId(e.target.value)}>
              <option value="">Select Driver</option>
              {drivers.map((drv) => (
                <option key={drv.DriverId} value={drv.DriverId}>
                  {drv.FullName || `Driver #${drv.DriverId}`}
                </option>
              ))}
            </select>

            <input className="input" placeholder="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} />

            <div className="row" style={{ gap: 10, alignItems: "center" }}>
              <input className="input" placeholder="Inventory ID" value={inventoryId} readOnly />
              <button className="btn-outline" onClick={() => setShowSearchModal(true)} disabled={busy}>
                Search Inventory
              </button>
            </div>

            <input className="input" placeholder="RO WO" value={rowo} onChange={(e) => setRowo(e.target.value)} />
            <input className="input" placeholder="Chassis VIN" value={chassisVin} onChange={(e) => setChassisVin(e.target.value)} />

            <button className="btn" onClick={createOrUpdateMove} disabled={busy}>
              {editMode ? "Update" : "Create"}
            </button>
          </div>

          {editMode ? (
            <div style={{ marginTop: 20 }}>
              <CardComponent
                record={selectedInvRec || (selectedInvId != null ? { id: selectedInvId } : {})}
                table="all_inventory"
              />
              {selectedInvId != null && selectedInvRec === null ? (
                <div className="muted" style={{ marginTop: 8 }}>
                  Couldn’t load inventory record #{selectedInvId}.
                </div>
              ) : null}
            </div>
          ) : null}

          <button className="btn-outline" onClick={closeModal} disabled={busy}>
            Close
          </button>
        </Modal>
      )}

      {showSearchModal && (
        <Modal onClose={() => setShowSearchModal(false)}>
          <h2>Search Inventory</h2>

          <div className="row" style={{ gap: 10 }}>
            <input
              className="input"
              placeholder="Search by RO/WO, Factory WO, Stock PO"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button className="btn" onClick={searchInventory} disabled={searchBusy}>
              Search
            </button>
          </div>

          {searchBusy ? <div className="muted">Searching...</div> : null}

          <div style={{ marginTop: 10, display: "grid", gap: 10 }}>
            {searchResults.map((result) => (
              <div
                key={result.id}
                className="card"
                style={{ cursor: "pointer", background: "rgba(255,255,255,0.02)" }}
                onClick={() => selectInventory(result)}
              >
                <div>ID: {result.id}</div>
                <div>RO/WO: {result.rowo || "(none)"}</div>
                <div>Factory WO: {result.factory_wo || "(none)"}</div>
                <div>Stock PO: {result.stock_po || "(none)"}</div>
              </div>
            ))}
            {searchResults.length === 0 && !searchBusy ? <div className="muted">No results found.</div> : null}
          </div>

          <button className="btn-outline" onClick={() => setShowSearchModal(false)}>
            Close
          </button>
        </Modal>
      )}
    </div>
  );
}